import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  RadioGroup,
  Radio,
  Stack,
  Text,
  VStack,
  Flex,
  Box,
} from '@chakra-ui/react';

import { useCustomerAddress } from '../state/CustomerAddressContext';
import type { CustomerAddress } from '../state/CustomerAddressContext';

interface IProps {
  initialSelected?: string | number;
  loading: boolean;
  onSubmit: (addressId: CustomerAddress['id'] | undefined) => void;
  onEdit: (address: CustomerAddress) => void;
  onNew: () => void;
  handleBack: () => void;
}

export default ({
  onEdit,
  onNew,
  handleBack,
  onSubmit,
  initialSelected,
  loading,
}: IProps): React.ReactElement => {
  const { t } = useTranslation();
  const { customerAddress } = useCustomerAddress();
  const [selectedId, setSelectedId] = useState<
    CustomerAddress['id'] | undefined
  >(initialSelected as string);

  const handleEdit = (address: CustomerAddress) => () => {
    onEdit(address);
  };

  const handleNewAddress = () => {
    onNew();
  };

  const handleSubmit = () => {
    onSubmit(selectedId);
  };

  return (
    <>
      <ModalHeader>{t('subscription.update_shipping')}</ModalHeader>
      <ModalCloseButton />
      <ModalBody
        sx={{
          overflowX: 'hidden',
          overflowY: 'hidden',
          maxHeight: 'fit-content',
        }}
      >
        <form id="list-form" className="bundleapp-modal__scroll-content">
          <VStack spacing={2} align="stretch">
            <RadioGroup value={selectedId} onChange={setSelectedId}>
              <Stack spacing={2}>
                {customerAddress?.map((address) => (
                  <Flex
                    key={address.id}
                    className="bundleapp-flex bundleapp-flex-align-center"
                    alignItems="flex-start"
                    p={1}
                  >
                    <Radio value={address.id} size="md">
                      <Box ml={4}>
                        <Text fontWeight="bold" fontSize="md">
                          {address.firstName} {address.lastName}
                        </Text>
                        <Text fontSize="sm">
                          {address.address1} {address.address2}
                          <br />
                          {address.city}
                          {address.province && `, ${address.province}`},{' '}
                          {address.zip}
                          <br />
                          {address.country}
                        </Text>
                      </Box>
                    </Radio>
                    <Button
                      ml="auto"
                      variant="ghost"
                      size="sm"
                      isDisabled={loading}
                      onClick={handleEdit(address)}
                      className="bundleapp-btn--text"
                    >
                      {t('titles.edit')}
                    </Button>
                  </Flex>
                ))}
              </Stack>
            </RadioGroup>
            <div className="bundleapp-flex bundleapp-flex-align-center">
              <Button
                variant="ghost"
                onClick={handleNewAddress}
                isDisabled={loading}
                leftIcon={<Text as="span">+</Text>}
                className="bundleapp-btn--text"
                justifyContent="flex-start"
                px={0}
              >
                {t('subscription.add_new_address')}
              </Button>
            </div>
          </VStack>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button mr="4" onClick={handleBack} variant="outlinedBig">
          {t('titles.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          isLoading={loading}
          isDisabled={loading}
          variant="blackBig"
        >
          {t('titles.continue')}
        </Button>
      </ModalFooter>
    </>
  );
};
